import { useEffect, useState } from "react";
import {
  Alert,
  Anchor,
  Container,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { GoogleLogin } from "@react-oauth/google";
import { Link, useSearchParams } from "react-router-dom";
import { useGoogleRegister } from "../hooks/useAuth";
import { authApi } from "../api/authApi";

type InviteStatus = "pending" | "consumed" | "revoked" | "expired";

export function RegisterPage() {
  const [params] = useSearchParams();
  const inviteToken = params.get("invite");
  const registerMutation = useGoogleRegister();
  const [validating, setValidating] = useState(true);
  const [inviteStatus, setInviteStatus] = useState<InviteStatus | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [registerError, setRegisterError] = useState<string | null>(null);

  useEffect(() => {
    if (!inviteToken) {
      setValidating(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await authApi.validateInvite(inviteToken);
        if (cancelled) return;
        setInviteStatus(res.status);
        if (res.status === "pending") {
          setInviteEmail(res.email ?? "");
        }
      } catch {
        if (!cancelled) setInviteStatus("revoked"); // generic invalid
      } finally {
        if (!cancelled) setValidating(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inviteToken]);

  // No token in URL
  if (!inviteToken) {
    return (
      <Container size={420} my={40}>
        <Title ta="center" order={1}>
          Registration is by invitation only
        </Title>
        <Text c="dimmed" size="sm" ta="center" mt="md">
          Have an account?{" "}
          <Anchor component={Link} to="/login">
            Sign in
          </Anchor>
        </Text>
      </Container>
    );
  }

  // Validating token
  if (validating) {
    return (
      <Container size={420} my={40}>
        <Stack align="center">
          <Loader />
        </Stack>
      </Container>
    );
  }

  // Token in invalid state
  if (inviteStatus !== "pending") {
    const message =
      inviteStatus === "consumed"
        ? "This invitation has already been used."
        : inviteStatus === "expired"
          ? "This invitation has expired. Ask for a new one."
          : "This invitation link is no longer valid.";
    return (
      <Container size={420} my={40}>
        <Title ta="center" order={1}>
          Invitation problem
        </Title>
        <Alert mt="md" color="orange">
          {message}
        </Alert>
        <Text c="dimmed" size="sm" ta="center" mt="md">
          Already have an account?{" "}
          <Anchor component={Link} to="/login">
            Sign in
          </Anchor>
        </Text>
      </Container>
    );
  }

  // Valid pending invite — show Google sign-up
  return (
    <Container size={420} my={40}>
      <Title ta="center" order={1}>
        Create your account
      </Title>
      <Text c="dimmed" size="sm" ta="center" mt={5}>
        Already have an account?{" "}
        <Anchor component={Link} to="/login">
          Sign in
        </Anchor>
      </Text>

      <Paper withBorder shadow="md" p={30} radius="md">
        <Stack gap="md">
          <Stack gap={4}>
            <Text size="sm" c="dimmed">Signing up as</Text>
            <Text fw={600}>{inviteEmail}</Text>
          </Stack>
          <Text size="xs" c="dimmed">
            Please sign in with the Google account for{' '}
            <Text span fw={600}>{inviteEmail}</Text>.
          </Text>
          {registerError && (
            <Alert color="red">{registerError}</Alert>
          )}
          <GoogleLogin
            onSuccess={(credentialResponse) => {
              if (!credentialResponse.credential || !inviteToken) return;
              setRegisterError(null);
              registerMutation.mutate(
                { idToken: credentialResponse.credential, inviteToken },
                {
                  onError: (err) =>
                    setRegisterError(err instanceof Error ? err.message : 'Registration failed'),
                }
              );
            }}
            onError={() => setRegisterError('Google sign-in failed. Please try again.')}
          />
        </Stack>
      </Paper>
    </Container>
  );
}
